import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { DataService } from './data.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { SignInService } from './twofa/signin.service';
import { DataDialogService } from './dialog/dataDialogService';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, Subscription, filter, finalize, of, switchMap, take, tap } from 'rxjs';
import { UserService } from './user.service';


@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  public idUser: string;
  private jwt = '';
  private role: string;
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private proactiveRefreshTimer: any = null;
  private readonly REFRESH_BUFFER_SECONDS = 120;

  constructor(
    private cookieService: CookieService,
    private router: Router,
    private dialogDataService: DataDialogService,
    private userService: UserService,
    private signInService: SignInService
  ) { }

  public getJwt() {
    this.jwt = this.cookieService.get(environment.jwtName) || '';
    if (this.jwt && this.jwt !== 'null' && this.jwt !== 'undefined') {
      const jwtHelper = new JwtHelperService();
      this.role = jwtHelper.decodeToken(this.jwt).role;
    } else {
      this.jwt = '';
      this.role = null;
    }
    return this.jwt;
  }

  public getRole() {
    this.getJwt();
    return this.role;
  }

  public isJwtExpired() {
    this.getJwt();
    if (this.jwt === '') return true;
    const jwtHelper = new JwtHelperService();
    return jwtHelper.isTokenExpired(this.jwt);
  }

  public getIdUser() {
    this.getJwt();
    if (this.jwt) {
      const jwtHelper = new JwtHelperService();
      this.idUser = jwtHelper.decodeToken(this.jwt).name;
    } else {
      this.idUser = null;
    }
    return this.idUser;
  }

  login(token: string) {
    this.jwt = token;
    this.cookieService.set(environment.jwtName, token, {
      expires: 1,
      path: '/',
      domain: null,
      secure: true,
      sameSite: 'Strict'
    });
    this.scheduleProactiveRefresh(token);
  }

  scheduleProactiveRefresh(token?: string) {
    this.clearProactiveRefresh();

    const jwt = token || this.getJwt();
    if (!jwt) return;

    try {
      const jwtHelper = new JwtHelperService();
      const decoded = jwtHelper.decodeToken(jwt);
      if (!decoded?.exp) return;

      const expiresAt = decoded.exp * 1000;
      const now = Date.now();
      const refreshIn = expiresAt - now - (this.REFRESH_BUFFER_SECONDS * 1000);

      if (refreshIn <= 0) {
        this.refreshAccessToken().subscribe();
        return;
      }

      this.proactiveRefreshTimer = setTimeout(() => {
        if (this.getJwt()) {
          this.refreshAccessToken().subscribe({
            error: () => {}
          });
        }
      }, refreshIn);
    } catch {
      // Token invalide
    }
  }

  clearProactiveRefresh() {
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
      this.proactiveRefreshTimer = null;
    }
  }

  initProactiveRefresh() {
    if (this.getJwt() && !this.isJwtExpired()) {
      this.scheduleProactiveRefresh();
    }
  }

  logout() {
    this.clearProactiveRefresh();
    // remove user from local storage and set current user to null
    this.dialogDataService.dialog.forEach((c) => c.close());
    this.dialogDataService.dialog = [];

    this.jwt = '';
    this.idUser = null;
    this.role = null;
    this.cookieService.delete(environment.jwtName, '/');
    this.cookieService.delete('refreshToken', '/');
    this.signInService.signOut().subscribe();

    this.router.navigate(['/login']);
  }

  refreshAccessToken(): Observable<string> {

    if (this.isRefreshing) {
      // Si déjà en cours, on attend qu’il soit terminé
      return this.refreshTokenSubject.pipe(
        filter(t => t !== null),
        take(1),
        switchMap(() => of(this.getJwt()))
      );
    }

    this.isRefreshing = true;
    this.refreshTokenSubject.next(null);

    return this.signInService.refreshToken().pipe(
      tap(value => {
        this.login(value!);
        this.refreshTokenSubject.next(value!);
      }),
      finalize(() => {
        this.isRefreshing = false;
      })
    );
  }
}
